import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "fl-theme";

/**
 * Three-mode theme: 'light' | 'dark' | 'system'.
 * - 'system' follows prefers-color-scheme live (responds to OS toggle).
 * - toggle() cycles light <-> dark and exits 'system' mode.
 * - setTheme() takes any of the three values.
 */
function _systemPrefersDark() {
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches || false;
  } catch { return false; }
}

function _applyEffectiveTheme(setting) {
  const effective = setting === "system"
    ? (_systemPrefersDark() ? "dark" : "light")
    : setting;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(effective);
  return effective;
}

export const ThemeProvider = ({ children }) => {
  const [theme, _setTheme] = useState(() => localStorage.getItem(STORAGE_KEY) || "light");
  const [effectiveTheme, setEffective] = useState(() =>
    _applyEffectiveTheme(localStorage.getItem(STORAGE_KEY) || "light"));

  useEffect(() => {
    const eff = _applyEffectiveTheme(theme);
    setEffective(eff);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Live-follow the OS while in 'system' mode
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => setEffective(_applyEffectiveTheme("system"));
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!["light", "dark", "system"].includes(next)) return;
    _setTheme(next);
  }, []);

  const toggle = useCallback(() => {
    // Cycle: light -> dark -> system -> light
    _setTheme((t) => (t === "light" ? "dark" : t === "dark" ? "system" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
